import React, { useEffect, useMemo, useRef, useState } from "react";
import apiRequestCall from "../../../../../apiHelper/globalHelper";
import exportApiCall from "../../../../../apiHelper/export";
import Pagination from "../../../../../formHelper/pagination";
import { all_routes } from "../../../../../router/backendComponents/admin/all_routes";
import Table from "../../../../../formHelper/list";
import Loader from "../../../../../formHelper/Loader";
import Search from "../../../../../formHelper/search";
import DateRangePicker from "react-bootstrap-daterangepicker";
import apiFileRequestCall from "../../../../../apiHelper/filleHelper";
import Success from "../../../../../formHelper/success";
import dayjs from "dayjs";
import Delete from "../../../../../formHelper/delete";
import FilterDropdown from "../../../../../formHelper/filterDropDown";

const POLL_INTERVAL = 2000;

const SaleReport: React.FC = () => {
  const backendRoute = all_routes.backend;

  const [statusLoading, setStatusLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [saleOrder, setSaleOrders] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [query, setQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [importProcessed, setImportProcessed] = useState<number | null>(null);
  const [importTotal, setImportTotal] = useState<number | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [showMessage, setShowMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [showStatusUpdateModal, setStatusUpdateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [branchFilter, setBranchFilter] = useState<number[]>([]);

  const [exporting, setExporting] = useState(false);

  const pollRef = useRef<number | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionStatus, setActionStatus] = useState("approved");

  const toggleSelectOne = (id: number, checked: boolean) => {
    setSelectedIds((prev) =>
      checked ? [...prev, id] : prev.filter((x) => x !== id)
    );
  };
  // selection state

  useEffect(() => {
    jobIdRef.current = importJobId;
  }, [importJobId]);

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      const ids = saleOrder.map((r) => Number(r.id)).filter(Boolean);
      setSelectedIds(ids);
    } else {
      setSelectedIds([]);
    }
  };

  // columns (select column first so checkboxes show at left)
  const columns = useMemo(
    () => [
      {
        key: "select",
        label: (
          <input
            type="checkbox"
            title="Select All"
            checked={
              saleOrder.length > 0 && selectedIds.length === saleOrder.length
            }
            ref={(el) => {
              if (!el) return;

              const total = saleOrder.length;
              const selectedCount = saleOrder.filter((r) =>
                selectedIds.includes(Number(r.id))
              ).length;

              el.indeterminate = selectedCount > 0 && selectedCount < total;
            }}
            onChange={(e) => toggleSelectAll(e.currentTarget.checked)}
          />
        ),

        render: (row: any) => {
          const id = Number(row.id);
          const isChecked = selectedIds.includes(id);

          return (
            <input
              type="checkbox"
              checked={isChecked}
              onChange={(e) => toggleSelectOne(id, e.currentTarget.checked)}
            />
          );
        },
      },

      {
        key: "id",
        label: "#",
        render: (_: any, i: number) => (currentPage - 1) * perPage + i + 1,
      },
      { key: "qtr", label: "QTR" },
      { key: "month", label: "Month" },
      { key: "fy_year", label: "Year" },
      { key: "invoice", label: "Invoice" },
      { key: "invoice_date", label: "Date" },
      { key: "order_no", label: "Order No." },
      { key: "customer_name", label: "Customer" },
      { key: "branch", label: "Branch" },
      { key: "description", label: "Description" },
      { key: "part_no", label: "Part No." },
      { key: "category", label: "Category" },
      { key: "principal_name", label: "Principal" },
      { key: "authorised", label: "Authorised" },
      { key: "qty", label: "Qty" },
      { key: "amount", label: "Amount" },

      {
        key: "status",
        label: "Status",
        render: (row: any) => {
          const value = row.status ?? "Approved";
          const color =
            value === "cancel"
              ? "badge bg-soft-danger"
              : "badge bg-soft-success";

          return <span className={color}>{value}</span>;
        },
      },
    ],
    // include dependencies used inside
    [currentPage, perPage, saleOrder, selectedIds]
  );

  // fetch paginated data
  const fetchSaleReport = async (page = currentPage, perPageSize = perPage) => {
    setStatusLoading(true);
    try {
      const res = await apiRequestCall("post", backendRoute.getSaleReport, {
        params: {
          page,
          per_page: perPageSize,
          q: query,
          branch_list: branchFilter,
          date_from: startDate,
          date_to: endDate,
        },
      });

      const resp = res?.data ?? {};
      const paginator = resp?.data ?? {};

      if (resp?.status || res?.code === 200) {
        setSaleOrders(paginator?.data || []);
        setCurrentPage(paginator?.current_page || 1);
        setPerPage(paginator?.per_page || 10);
        setTotalItems(paginator?.total || 0);
      } else {
        setSaleOrders([]);
        setCurrentPage(1);
        setPerPage(10);
        setTotalItems(0);
      }
    } finally {
      setStatusLoading(false);
    }
  };

  const stopPollingStatus = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPollingStatus = (jobId: string) => {
    // prevent double-intervals
    if (pollRef.current) return;

    // ensure the ref holds the current job id
    jobIdRef.current = jobId;
    setImportJobId(jobId);

    const poller = async () => {
      try {
        let res;
        try {
          res = await apiRequestCall(
            "post",
            backendRoute.saleImportImportStatus,
            {
              params: { id: jobIdRef.current },
            }
          );
        } catch (err) {
          res = await apiRequestCall(
            "post",
            backendRoute.saleImportImportStatus,
            {
              data: { id: jobIdRef.current },
            }
          );
        }

        const data = res?.data ?? {};

        const status = data?.data?.status;
        const processed = data?.data?.processed_rows;
        const total = data?.data?.total_rows;
        const message = data?.message ?? data?.data?.message ?? null;

        setImportStatus(status ?? String(data));
        setImportProcessed(processed);
        setImportTotal(total);
        setImportMessage(message);

        const finishedStates = [
          "completed",
          "done",
          "failed",
          "error",
          "finished",
          "success",
        ];

        if (status && finishedStates.includes(String(status).toLowerCase())) {
          stopPollingStatus();
          jobIdRef.current = null;
          setImportJobId(null);
          setShowSuccess(true);
          setUploading(false);
          fetchSaleReport(1, perPage);
          setShowMessage("Import completed successfully.");
        }
      } catch (e) {
        setImportMessage("Unable to fetch status");
      }
    };

    poller();
    pollRef.current = window.setInterval(
      poller,
      POLL_INTERVAL
    ) as unknown as number;
  };

  useEffect(() => {
    return () => {
      stopPollingStatus();
    };
  }, []);

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (uploading) {
      alert("An import is already in progress. Please wait.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setImportStatus("starting");
    setImportProcessed(null);
    setImportTotal(null);
    setImportMessage(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const res = await apiFileRequestCall(
        "post",
        backendRoute.importSaleData,
        { data: formData }
      );

      const payload = res?.data ?? {};
      const ok = payload?.status || payload?.success || payload?.code === 200;

      const jobId =
        payload?.data?.job_id ||
        payload?.job_id ||
        payload?.data?.jobId ||
        payload?.jobId ||
        payload?.data?.id ||
        payload?.id ||
        null;

      if (ok && jobId) {
        setImportStatus(payload?.data?.status || "queued");
        startPollingStatus(String(jobId));
      } else if (ok && !jobId) {
        setImportStatus("completed");
        setImportMessage(payload?.message || "Import completed");
        fetchSaleReport(1, perPage);
      } else {
        setImportStatus("failed");
        setImportMessage(payload?.message || "Import failed");
      }
    } catch (err) {
      console.error("Import error:", err);
      setImportStatus("failed");
      setImportMessage("Something went wrong while importing");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const exportFile = async () => {
    try {
      setExporting(true);
      const result = await exportApiCall("post", backendRoute.exportSaleData, {
        data: {
          q: query,
          date_from: startDate,
          branch_list: branchFilter,
          date_to: endDate,
        },
        responseType: "blob",
      });

      if (result) {
        const blob = new Blob([result], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `performance-reports-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:T]/g, "-")}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setExporting(false);
      }
    } catch (error) {
      console.error("Failed to export file:", error);
    }
  };

  const bulkUpdateStatus = async (status: any) => {
    if (!selectedIds.length) return;
    setStatusUpdateModal(true);
    setActionStatus(status);
  };

  const bulkUpdateDelete = async (status: any) => {
    setShowDeleteModal(true);
    setActionStatus(status);
  };

  const updateInvoiceStatus = async () => {
    if (!selectedIds) return;
    const result = await apiRequestCall(
      "post",
      backendRoute.updateInvoiceStatus,
      {
        data: { ids: selectedIds, status: actionStatus },
      }
    );
    if (result?.data?.code === 200) {
      fetchSaleReport(currentPage, perPage);
      setShowMessage(result?.data?.message);
      setShowSuccess(true);
    }
    setSelectedIds([]);
    setStatusUpdateModal(false);
  };

  const confirmDelete = async () => {
    const result = await apiRequestCall(
      "post",
      backendRoute.deleteSaleRecords,
      {
        data: { ids: selectedIds, status: actionStatus },
      }
    );
    if (result?.data?.code === 200) {
      fetchSaleReport(currentPage, perPage);
      setShowMessage(result?.data?.message);
      setShowSuccess(true);
    }
    setSelectedIds([]);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    fetchSaleReport(currentPage, perPage);
  }, [currentPage, perPage, query, startDate, endDate, branchFilter]);

  const renderImportStatusBox = () => {
    if (!importStatus && !uploading) return null;

    const percent =
      importProcessed != null && importTotal
        ? Math.round((importProcessed / Math.max(1, importTotal)) * 100)
        : null;

    return (
      <div className="mb-3">
        <div className="card p-2">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <strong>Import Status :</strong>{" "}
              {String(importStatus || (uploading ? "uploading" : "idle"))}
              {importMessage ? ` — ${importMessage}` : ""}
            </div>
            <div>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => {
                  if (!importJobId) {
                    setImportStatus(null);
                    setImportMessage(null);
                  } else {
                    alert(
                      "Job running — wait for completion or check server to cancel."
                    );
                  }
                }}
              >
                Dismiss
              </button>
            </div>
          </div>

          {percent != null && (
            <div className="progress mt-2" style={{ height: 10 }}>
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${percent}%` }}
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {percent}%
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* compact header: title | export/import */}
        <div className="row page-header mb-2">
          <div className="col-12">
            <div className="d-flex align-items-start justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <h4 className="page-title">
                  Sale Reports
                  <span className="count-title ms-2">{totalItems}</span>
                </h4>
              </div>

              <div className="d-flex align-items-center gap-2 flex-nowrap overflow-auto">
                {/* Export / Import */}
                <div className="d-flex gap-2 flex-nowrap">
                  <button
                    className={`btn btn-soft-success d-flex align-items-center justify-content-center ${
                      exporting ? "disabled" : ""
                    }`}
                    onClick={exporting ? undefined : exportFile}
                    disabled={exporting}
                  >
                    <i
                      className={`me-1 ${
                        exporting
                          ? "ti ti-loader ti-spin"
                          : "ti ti-file-type-xls"
                      }`}
                    ></i>
                    <span>{exporting ? "Generating..." : "Export"}</span>
                  </button>

                  <label className="btn btn-soft-warning mb-0 d-flex align-items-center">
                    {uploading || importJobId ? (
                      <div className="spinner-border spinner-border-sm text-primary" />
                    ) : (
                      <i className="ti ti-upload"></i>
                    )}

                    <span className="d-none d-md-inline ms-1">
                      Import Excel
                    </span>

                    <input
                      type="file"
                      hidden
                      accept=".xlsx,.xls,.csv"
                      onChange={handleImport}
                    />
                  </label>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2 flex-nowrap">
                  {/* Cancel */}
                  <button
                    type="button"
                    className="btn btn-soft-danger d-flex align-items-center"
                    onClick={() => bulkUpdateStatus("cancel")}
                    disabled={selectedIds.length === 0}
                  >
                    <i className="ti ti-thumb-down"></i>

                    <span className="d-none d-md-inline ms-1">Cancel</span>

                    {selectedIds.length > 0 && (
                      <span className="ms-1">({selectedIds.length})</span>
                    )}
                  </button>

                  {/* Approve */}
                  <button
                    type="button"
                    className="btn btn-soft-success d-flex align-items-center"
                    onClick={() => bulkUpdateStatus("approved")}
                    disabled={selectedIds.length === 0}
                  >
                    <i className="ti ti-thumb-up"></i>

                    <span className="d-none d-md-inline ms-1">Approve</span>

                    {selectedIds.length > 0 && (
                      <span className="ms-1">({selectedIds.length})</span>
                    )}
                  </button>

                  {/* Delete */}
                  <button
                    type="button"
                    className="btn btn-soft-primary d-flex align-items-center"
                    onClick={() => bulkUpdateDelete("flush_all")}
                  >
                    <i className="ti ti-trash"></i>

                    <span className="d-none d-md-inline ms-1">Delete</span>

                    {selectedIds.length > 0 && (
                      <span className="ms-1">({selectedIds.length})</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top card: search / date */}
        <div className="card mb-2">
          <div className="card-body">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md-4">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-search" />
                  </span>
                  <Search
                    placeholder="Search"
                    value={query}
                    onChange={(v) => {
                      setQuery(v);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div className="icon-form">
                  <span className="form-icon">
                    <i className="ti ti-calendar" />
                  </span>
                  <DateRangePicker
                    initialSettings={{
                      autoUpdateInput: false,
                      locale: { cancelLabel: "Clear" },
                    }}
                    onCallback={(start, end) => {
                      setStartDate(start.format("YYYY-MM-DD"));
                      setEndDate(end.format("YYYY-MM-DD"));
                      setCurrentPage(1);
                    }}
                    onCancel={() => {
                      setStartDate("");
                      setEndDate("");
                      setCurrentPage(1);
                    }}
                  >
                    <input
                      type="text"
                      className="form-control bookingrange"
                      placeholder="Select Date Range Filter"
                      value={
                        startDate && endDate
                          ? `${dayjs(startDate).format("DD-MM-YYYY")} - ${dayjs(
                              endDate
                            ).format("DD-MM-YYYY")}`
                          : ""
                      }
                      readOnly
                      style={{ cursor: "pointer" }}
                      aria-label="Select date range"
                    />
                  </DateRangePicker>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <FilterDropdown
                  label="Branch"
                  endpoint="/getBranchDD"
                  selected={branchFilter}
                  onChange={(v) => setBranchFilter(v as number[])}
                />
              </div>

              <div className="col-12 col-md-4 d-flex justify-content-end">
                {/* you can add more top-right controls here if needed */}
              </div>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="card">
          <div className="card-body">
            {/* Import status box */}
            {renderImportStatusBox()}

            <div className="table-responsive">
              {statusLoading ? (
                <Loader />
              ) : (
                <Table data={saleOrder} columns={columns} />
              )}
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            onPerPageChange={(size) => {
              setPerPage(size);
              setCurrentPage(1);
            }}
            perPageOptions={[
              { value: 5, label: "5" },
              { value: 10, label: "10" },
              { value: 25, label: "25" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
              { value: 500, label: "500" },
              { value: 1000, label: "1000" },
            ]}
          />
        </div>

        <Success
          id="formatSuccessModal"
          title="Invoice"
          message={showMessage}
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
        />

        <Delete
          show={showStatusUpdateModal}
          title="Change Status"
          message="Are you sure ? You want update the status of invoice"
          onConfirm={updateInvoiceStatus}
          onCancel={() => setStatusUpdateModal(false)}
        />

        <Delete
          show={showDeleteModal}
          title="Delete Records"
          message="Are you sure ? You wanted to remove the records ?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default SaleReport;
